import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {Modal} from "../../../../../services/modal.service";
import {StorageService} from "src/modules/common/services/storage.service";
import {UserService} from "src/modules/common/services/user.service";
import {ToastService} from "src/modules/common/services/toast.service";
import {DateRangeMomentValue} from "src/modules/common/components/form/date/range.component";
import * as moment from "moment";

export namespace LogsFilter {

    export type TOperator = "AND" | "OR";

    export interface IRule {
        key: string;
        value: string;
        operator: TOperator;
    }

    /**
     * Full filter state persisted for a user: the standard date-range / level
     * controls plus the advanced ad-hoc rules.
     */
    export interface IState {
        date_range: string | null;
        level: string | null;
        rules: IRule[];
    }

    export const LEVELS: string[] = [
        "DEBUG",
        "INFO",
        "NOTICE",
        "WARNING",
        "ERROR",
        "CRITICAL",
        "ALERT",
        "EMERGENCY"
    ];

    /**
     * Build the per-user local-storage key under which the log filters are persisted.
     * @param {number | string} userId
     * @returns {string}
     */
    export function storageKey(userId: number | string): string {
        return `logs_filters_${userId ?? "anon"}`;
    }

    /**
     * Normalise whatever is in storage into a full state object. Tolerates the
     * legacy shape where only the rules array was stored.
     * @param persisted
     * @returns {IState}
     */
    export function toState(persisted: any): IState {
        if (Array.isArray(persisted)) {
            return {date_range: null, level: null, rules: persisted};
        }
        return {
            date_range: persisted?.date_range ?? null,
            level: persisted?.level ?? null,
            rules: persisted?.rules ?? []
        };
    }
}

@Component({
    selector: "logs-filter",
    template: `
        <h1 class="container-heading">Log filters</h1>
        <mat-card>
            <mat-card-content>
                <form [formGroup]="formGroup" autocomplete="off" novalidate>
                    <div class="group flex">

                        <common-form-date-range label="Time" [value]="dateRangeValue"
                                                class="third2"
                                                (valueChange)="dateRange = $event">
                        </common-form-date-range>

                        <mat-form-field class="third">
                            <mat-label>Level</mat-label>
                            <mat-select formControlName="level">
                                <mat-option [value]="null">All</mat-option>
                                <mat-option *ngFor="let l of levels" [value]="l">{{ l }}</mat-option>
                            </mat-select>
                        </mat-form-field>


                        <ng-container formArrayName="rules"
                                      *ngFor="let rule of rules.controls; let i = index; let first = first">
                            <div class="full rule-row" [formGroupName]="i">

                                <mat-form-field class="operator" *ngIf="!first">
                                    <mat-label>Logic</mat-label>
                                    <mat-select formControlName="operator">
                                        <mat-option *ngFor="let op of operators" [value]="op">{{ op }}</mat-option>
                                    </mat-select>
                                </mat-form-field>
                                <span class="operator-spacer" *ngIf="first">Where</span>

                                <mat-form-field class="key">
                                    <mat-label>Parameter</mat-label>
                                    <input matInput formControlName="key" [matAutocomplete]="keyAuto"
                                           placeholder="e.g. context.partner">
                                    <mat-autocomplete #keyAuto="matAutocomplete">
                                        <mat-option *ngFor="let s of keySuggestions" [value]="s">{{ s }}</mat-option>
                                    </mat-autocomplete>
                                </mat-form-field>

                                <mat-form-field class="value">
                                    <mat-label>Value</mat-label>
                                    <input matInput formControlName="value" placeholder="value">
                                </mat-form-field>

                                <button mat-icon-button color="warn" type="button" (click)="removeRule(i)"
                                        matTooltip="Remove filter"
                                        class="text-center">
                                    <mat-icon>delete</mat-icon>
                                </button>

                            </div>
                        </ng-container>

                    </div>


                </form>
            </mat-card-content>
        </mat-card>

        <div class="text-right">
            <button mat-raised-button color="accent" (click)="addRule()">
                <mat-icon>add</mat-icon>
                Add filter
            </button>
        </div>

        <div class="footer">
            <button mat-button (click)="cancel()">Cancel</button>
            <button mat-button color="warn" (click)="clearAll()">Clear all</button>
            <button mat-raised-button color="primary" (click)="save()">Save & apply</button>
        </div>
    `,
    styles: [
        `
            .rule-row {
                display: flex;
                align-items: baseline;
                justify-content: space-between;
                gap: 12px;
            }

            .rule-row .operator {
                width: 110px;
            }

            .rule-row .operator-spacer {
                width: 110px;
                color: #757575;
            }

            .rule-row .key {
                flex: 1;
            }

            .rule-row .value {
                flex: 1;
            }

            .rule-row button {
                margin-right: 0;
            }

            .footer {
                display: flex;
                justify-content: flex-end;
                gap: 12px;
                margin-top: 25px;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LogsFilterComponent implements OnInit {

    public modal: Modal.IModal;

    public formGroup: FormGroup = new FormGroup({
        level: new FormControl(null),
        rules: new FormArray([])
    });

    /**
     * Date range as the "start ~ end" string the API expects; kept outside the
     * form because common-form-date-range works via value/valueChange, not a control.
     */
    public dateRange: string | null = null;

    /**
     * Initial value fed back into the date-range picker so a persisted range shows.
     */
    public dateRangeValue: DateRangeMomentValue = {start: null, end: null};

    public readonly levels: string[] = LogsFilter.LEVELS;

    public readonly operators: LogsFilter.TOperator[] = ["AND", "OR"];

    public readonly keySuggestions: string[] = [
        "host",
        "level",
        "message",
        "component",
        "run_id",
        "context.at",
        "context.partner",
        "context.user_id",
        "context.order_id"
    ];

    public constructor(
        private storageService: StorageService,
        private userService: UserService,
        private toastService: ToastService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
    }

    public get rules(): FormArray {
        return this.formGroup.get("rules") as FormArray;
    }

    private get storageKey(): string {
        return LogsFilter.storageKey(this.userService.data?.id);
    }

    /**
     * Load persisted state — prefer any passed in by the caller, fall back to
     * local storage — and hydrate the controls.
     */
    private load(): void {
        const state: LogsFilter.IState = LogsFilter.toState(
            this.modal?.params?.state ?? this.storageService.get(this.storageKey)
        );

        this.dateRange = state.date_range;
        if (this.dateRange && this.dateRange.includes("~")) {
            const [from, to] = this.dateRange.split("~").map((part: string): string => part.trim());
            this.dateRangeValue = {start: moment(from), end: moment(to)};
        }

        this.formGroup.get("level").setValue(state.level, {emitEvent: false});

        for (const rule of state.rules) {
            this.addRule(rule);
        }
    }

    public addRule(rule?: LogsFilter.IRule): void {
        this.rules.push(new FormGroup({
            operator: new FormControl(rule?.operator ?? "AND"),
            key: new FormControl(rule?.key ?? null, [Validators.required]),
            value: new FormControl(rule?.value ?? null)
        }));
        this.changeDetectorRef.markForCheck();
    }

    public removeRule(index: number): void {
        this.rules.removeAt(index);
        this.changeDetectorRef.markForCheck();
    }

    private buildState(): LogsFilter.IState {
        // drop rows with an empty parameter so we never persist blank filters
        const rules: LogsFilter.IRule[] = (this.rules.value as LogsFilter.IRule[])
            .filter((rule: LogsFilter.IRule): boolean => !!rule.key && rule.key.trim().length > 0)
            .map((rule: LogsFilter.IRule): LogsFilter.IRule => ({
                operator: rule.operator || "AND",
                key: rule.key.trim(),
                value: rule.value ?? ""
            }));

        return {
            date_range: this.dateRange || null,
            level: this.formGroup.get("level").value ?? null,
            rules
        };
    }

    private apply(state: LogsFilter.IState, message: string): void {
        this.storageService.set(this.storageKey, state);
        this.toastService.show(message, "success");
        this.modal.response.emit({name: "save", value: state});
    }

    public clearAll(): void {
        this.rules.clear();
        this.formGroup.get("level").setValue(null);
        this.dateRange = null;
        this.dateRangeValue = {start: null, end: null};
        this.changeDetectorRef.markForCheck();

        this.apply({date_range: null, level: null, rules: []}, "Filters cleared");
    }

    public save(): void {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            this.toastService.show("Every advanced filter needs a parameter", "warning");
            return;
        }

        this.apply(this.buildState(), "Filters saved");
    }

    public cancel(): void {
        this.modal.response.emit({name: "cancel"});
    }

    public ngOnInit(): void {
        this.load();
    }
}